import { useState } from 'react';

import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';

import type { Media } from '@/lib/schemas/post';
import { radius } from '@/theme';

interface PostMediaProps {
  media: Media[];
}

/**
 * Media layout from spec §9.2: one image runs full width capped at 16:9, two sit
 * side by side, three or four form a grid.
 *
 * Video never autoplays. It shows a poster (once the ffmpeg worker in Milestone
 * 8 produces one) with an explicit play control, so nothing starts moving or
 * making noise without being asked.
 */
export function PostMedia({ media }: PostMediaProps) {
  if (media.length === 0) return null;

  const columns = media.length === 1 ? 1 : 2;
  // Three items: the first spans the full width above a pair.
  const firstSpansRow = media.length === 3;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 0.75,
        borderRadius: `${radius.card}px`,
        overflow: 'hidden',
      }}
    >
      {media.map((item, index) => (
        <Box
          key={item.id}
          sx={{
            gridColumn: firstSpansRow && index === 0 ? '1 / -1' : undefined,
            position: 'relative',
            aspectRatio: media.length === 1 ? '16 / 9' : '1 / 1',
            backgroundColor: 'action.hover',
            overflow: 'hidden',
          }}
        >
          {item.kind === 'IMAGE' ? (
            <PostImage media={item} single={media.length === 1} />
          ) : (
            <PostVideo media={item} />
          )}
        </Box>
      ))}
    </Box>
  );
}

function PostImage({ media, single }: { media: Media; single: boolean }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <>
      {/* Placeholder tint while the image arrives, so the card does not flash white. */}
      {!loaded && (
        <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'action.selected' }} />
      )}
      <Box
        component="img"
        // The small variant is plenty for a grid cell; only a solo image needs the large one.
        src={(single ? media.largeUrl : media.smallUrl) ?? media.url}
        alt=""
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 200ms ease',
        }}
      />
    </>
  );
}

function PostVideo({ media }: { media: Media }) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <Box
        component="video"
        src={media.url}
        controls
        autoPlay
        playsInline
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#000',
        }}
      />
    );
  }

  return (
    <>
      {media.thumbnailUrl && (
        <Box
          component="img"
          src={media.thumbnailUrl}
          alt=""
          loading="lazy"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
      <IconButton
        onClick={() => setPlaying(true)}
        aria-label={t('post.playVideo')}
        sx={{
          position: 'absolute',
          inset: 0,
          margin: 'auto',
          width: 56,
          height: 56,
          color: '#fff',
          backgroundColor: 'rgba(0,0,0,0.45)',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
        }}
      >
        <PlayCircleFilledRoundedIcon sx={{ fontSize: 36 }} />
      </IconButton>
    </>
  );
}
